import React from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ScopeConstraints } from '../../../../../models/FormProps';
import { getLevelDisplayName } from '../../../../../utils/levels';
import { getAudienceTypeDisplayName } from '../../../../../utils/audiences';

type ScopeConstraintBannerProps = {
  scopeConstraints: ScopeConstraints | null;
};

// Warning severity rather than informational: the banner's weight should match
// the fact that the constraint is a hard gate, not advice. It renders its own
// container because it sits above each step's layout rather than inside it, and
// so has to line the page's gutters up itself.
//
// It names the pinned audience type as well as the levels, because the pin is
// otherwise completely silent: a pinned form creator reaches the audience-type
// and targeted/untargeted steps to find one preselected button and a hidden
// Back button, with nothing saying why. Only the template's pin is named - an
// integration-sourced pin stays as silent as it has always been.
export default function ScopeConstraintBanner({ scopeConstraints }: ScopeConstraintBannerProps): JSX.Element | null {
  const theme = useTheme();

  const pinnedAudienceName = getAudienceTypeDisplayName(scopeConstraints?.audienceType);
  const allowedLevelNames = (scopeConstraints?.allowedLevelCodes ?? [])
    .map(code => getLevelDisplayName(code))
    .filter((name): name is string => name !== null);

  // With no pin and no level to name there is nothing to say, and the page
  // looks exactly as it does today.
  if (pinnedAudienceName === null && allowedLevelNames.length === 0) return null;

  return (
    <Container>
      <Alert
        severity="warning"
        icon={<LockOutlinedIcon />}
        sx={{ color: theme.palette.text.primary, marginBottom: 2 }}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>This template restricts audience scope</AlertTitle>
        {pinnedAudienceName !== null && (
          <Typography variant="body2">{`This form can only be sent to ${pinnedAudienceName}.`}</Typography>
        )}
        {allowedLevelNames.length > 0 && (
          <Typography variant="body2">{`You can only select from: ${allowedLevelNames.join(', ')}`}</Typography>
        )}
      </Alert>
    </Container>
  );
}
